import React, { useState } from 'react';
import { ApolloProvider, Query } from 'react-apollo';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';

import createApolloClient from '../utils/createApolloClient';
import Map from './Map';
import MapContainer from './Map/Container';
import ErrorMessage from './Map/ErrorMessage';
import { MAP_CHANNEL, LANGUAGES, SEASON_COLOR } from '../constants/app';

const POIS_MAP_STYLE_QUERY = gql`
    query POIS_MAP_STYLE_QUERY($channel: String!) {
        pois(channel: $channel) {
            id
            name
            short_description
            description
            category
            minimal_length
            audiences {
                id
            }
            coordinates {
                latitude
                longitude
            }
            accessibility_features {
                id
                name
            }
            area {
                id
            }
            cover {
                src
            }
            more_info_web_link
        }
        map {
            style {
                ... on MapBoxStyle {
                    styleUrl
                }
            }
        }
    }
`;

const MapProvider = ({ fullscreen: isFullscreen, season, gql: graphql, errorMessages, ...rest }) => {
    const [fullscreen, setFullscreen] = useState(!!isFullscreen);
    return (
        <ApolloProvider client={createApolloClient(graphql)}>
            <MapContainer season={season} fullscreen={fullscreen}>
                <Query query={POIS_MAP_STYLE_QUERY} variables={{ channel: MAP_CHANNEL }}>
                    {({ data, error, loading }) => {
                        if (loading) return null; // loading
                        if (error) return <ErrorMessage error={error} copy={errorMessages.default} />;

                        const { pois, map } = data;
                        const uniqPoisById = uniqBy(pois, 'id'); // Remove POIs with duplicate ids
                        return (
                            <Map
                                styleUrl={map.style.styleUrl}
                                pois={uniqPoisById.filter(poi => !!poi.coordinates)} // POIs filter by having coordinates
                                setFullscreen={setFullscreen}
                                fullscreen={fullscreen}
                                {...rest}
                            />
                        );
                    }}
                </Query>
            </MapContainer>
        </ApolloProvider>
    );
};

MapProvider.propTypes = {
    fullscreen: PropTypes.bool,
    season: PropTypes.oneOf(Object.keys(SEASON_COLOR)).isRequired,
    gql: PropTypes.shape({
        uri: PropTypes.string.isRequired,
        language: PropTypes.oneOf(LANGUAGES).isRequired,
        key: PropTypes.string.isRequired,
    }).isRequired,
    errorMessages: PropTypes.objectOf(PropTypes.string).isRequired,
};

export { MapProvider };
